import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import TrafficPanel from "@/components/admin/TrafficPanel";
import GoogleInsights from "@/components/admin/google/GoogleInsights";
import { TrendingUp, Eye, Search, Globe, FileText } from "lucide-react";

export default function AdminSEO() {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("google");

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-white/40">Loading…</div>;
  const maxViews = Math.max(1, ...stats.by_category.map((c) => c.views));

  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl font-bold text-white">SEO & Traffic</h1>
      <p className="text-sm text-white/50">Monitor content performance and search visibility.</p>

      <div className="mt-5 flex gap-2 border-b border-white/10" role="tablist">
        {[["google", "Google data"], ["site", "Website visitors"], ["content", "Content"]].map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`seo-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-bold -mb-px border-b-2 transition-colors ${tab === k ? "border-[hsl(var(--primary))] text-white" : "border-transparent text-white/50 hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {tab === "google" && <GoogleInsights />}
      {tab === "site" && <TrafficPanel />}
      {tab === "content" && <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-5">
        <Stat icon={Eye} label="Total Article Views" value={stats.total_views?.toLocaleString()} />
        <Stat icon={FileText} label="Indexed Articles" value={stats.published} />
        <Stat icon={TrendingUp} label="Avg. Views / Post" value={Math.round(stats.total_views / Math.max(1, stats.published)).toLocaleString()} />
      </div>

      <div className="card-surface rounded-xl p-5 mt-5">
        <h3 className="font-bold text-white mb-4 flex items-center gap-2"><TrendingUp size={16} /> Article views by Category</h3>
        <div className="space-y-3">
          {stats.by_category.map((c) => (
            <div key={c.category}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-white/70">{c.category}</span>
                <span className="text-white/40">{c.views.toLocaleString()} views</span>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(c.views / maxViews) * 100}%`, background: "hsl(var(--accent))" }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card-surface rounded-xl p-5 mt-5">
        <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Search size={16} /> Search Appearance Preview</h3>
        <div className="p-4 rounded-lg bg-black/30 border border-white/10">
          <div className="text-[hsl(var(--primary))] text-lg">{settings?.seo?.meta_title || "7 Legendary"}</div>
          <div className="text-green-500/80 text-xs mt-0.5">{window.location.origin}</div>
          <div className="text-white/60 text-sm mt-1">{settings?.seo?.meta_description}</div>
        </div>
        <p className="text-xs text-white/40 mt-3 flex items-center gap-1.5"><Globe size={13} /> Edit meta tags under <strong className="text-white/60">Settings → SEO Meta</strong>. Each article generates its own SEO-friendly URL slug.</p>
      </div>
      </>}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card-surface rounded-xl p-5">
      <Icon size={18} className="text-[hsl(var(--primary))]" />
      <div className="mt-3 text-2xl font-bold font-head text-white">{value}</div>
      <div className="text-xs text-white/50 mt-1">{label}</div>
    </div>
  );
}
